import * as XLSX from 'xlsx';

/**
 * Normalize questions from either SurveyQuestion or QuestionBuilder format
 * into a common format for export.
 */
export const normalizeQuestions = (questions, source) => {
  if (!questions || questions.length === 0) return [];

  if (source === 'survey') {
    return [...questions]
      .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
      .map((q, idx) => ({
        questionNumber: idx + 1,
        questionText: q.question_text || '',
        mediaUrl: q.media?.url || '',
        media: q.media ? { file_key: q.media.file_key || '', media_type: q.media.media_type || '', filename: q.media.filename || '' } : null,
        followups: (q.followups || [])
          .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
          .map((f) => ({
            text: f.text || '',
            mediaUrl: f.media?.url || '',
            media: f.media ? { file_key: f.media.file_key || '', media_type: f.media.media_type || '', filename: f.media.filename || '' } : null,
          })),
      }));
  }

  // agent (QuestionBuilder parsed objects)
  return questions.map((q, idx) => ({
    questionNumber: idx + 1,
    questionText: q.text || '',
    mediaUrl: q.media?.url || '',
    media: q.media ? { file_key: q.media.file_key || '', media_type: q.media.media_type || '', filename: q.media.filename || '' } : null,
    followups: (q.followups || []).map((f) => ({
      text: f.text || '',
      mediaUrl: f.media?.url || '',
      media: f.media ? { file_key: f.media.file_key || '', media_type: f.media.media_type || '', filename: f.media.filename || '' } : null,
    })),
  }));
};

/**
 * Download questions as Excel (.xls).
 * Layout: # | Question | Q Media | Follow-up 1 | F1 Media | Follow-up 2 | F2 Media | ...
 * Media cells contain URL + {{file_key|media_type|filename}} metadata for re-import.
 */
export const downloadAsExcel = (normalizedQuestions, filename) => {
  if (!normalizedQuestions || normalizedQuestions.length === 0) return;

  const maxFollowups = Math.max(...normalizedQuestions.map((q) => q.followups.length), 0);
  const headers = ['#', 'Question', 'Q Media'];
  for (let i = 0; i < maxFollowups; i++) {
    headers.push(`Follow-up ${i + 1}`, `F${i + 1} Media`);
  }

  const headerRow = `<tr>${headers.map((h) => `<th style="font-weight:bold;background:#f3f4f6">${escapeHtml(h)}</th>`).join('')}</tr>`;
  const rows = normalizedQuestions.map((q) => {
    const qMediaVal = buildMediaCellValue(q.mediaUrl, q.media);
    let cells = `<td>${q.questionNumber}</td><td>${escapeHtml(q.questionText)}</td><td>${escapeHtml(qMediaVal)}</td>`;
    for (let i = 0; i < maxFollowups; i++) {
      const f = q.followups[i];
      const fMediaVal = f ? buildMediaCellValue(f.mediaUrl, f.media) : '';
      cells += `<td>${escapeHtml(f?.text || '')}</td><td>${escapeHtml(fMediaVal)}</td>`;
    }
    return `<tr>${cells}</tr>`;
  });

  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
    <head><meta charset="UTF-8"></head>
    <body><table border="1">${headerRow}${rows.join('')}</table></body>
    </html>`;

  const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
  downloadBlob(blob, `${sanitizeFilename(filename)}_questions.xls`);
};

/**
 * Download questions as Text (.txt).
 * Media lines include {{file_key|media_type|filename}} metadata for re-import.
 */
export const downloadAsText = (normalizedQuestions, filename) => {
  if (!normalizedQuestions || normalizedQuestions.length === 0) return;

  const lines = normalizedQuestions.map((q) => {
    let text = `Q${q.questionNumber}: ${q.questionText}`;
    if (q.mediaUrl || q.media) {
      text += `\n  Q${q.questionNumber} Media: ${buildMediaCellValue(q.mediaUrl, q.media)}`;
    }
    q.followups.forEach((f, i) => {
      if (i > 0 || q.mediaUrl || q.media) text += '\n';
      text += `\n  F${i + 1}: ${f.text || f}`;
      if (f.mediaUrl || f.media) {
        text += `\n  F${i + 1} Media: ${buildMediaCellValue(f.mediaUrl, f.media)}`;
      }
    });
    return text;
  });

  const blob = new Blob([lines.join('\n\n') + '\n'], { type: 'text/plain;charset=utf-8;' });
  downloadBlob(blob, `${sanitizeFilename(filename)}_questions.txt`);
};

/**
 * Parse a text file into question objects.
 * Format: Q{n}: text, indented F{n}: follow-up. Fallback: each line = question.
 * Media lines with {{file_key|type|name}} metadata are parsed and attached.
 */
export const parseTextImport = (content) => {
  const lines = content.split(/\r?\n/);
  const questions = [];
  let currentQuestion = null;
  let hasQPrefix = false;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    // Media lines — extract metadata if present
    if (/^(Q\d+|F\d+)\s+Media:/i.test(trimmed)) {
      const mediaRef = parseMediaMeta(trimmed);
      if (mediaRef && currentQuestion) {
        if (/^Q\d+/i.test(trimmed)) {
          currentQuestion.media = mediaRef;
        } else {
          // Attach to last followup
          const lastIdx = currentQuestion.followups.length - 1;
          if (lastIdx >= 0) {
            const last = currentQuestion.followups[lastIdx];
            if (typeof last === 'string') {
              currentQuestion.followups[lastIdx] = { text: last, media: mediaRef };
            } else {
              last.media = mediaRef;
            }
          }
        }
      }
      continue;
    }

    const qMatch = trimmed.match(/^Q\d+[:.]\s*(.+)/i);
    const fMatch = trimmed.match(/^F\d+[:.]\s*(.+)/i);

    if (qMatch) {
      hasQPrefix = true;
      if (currentQuestion) questions.push(currentQuestion);
      currentQuestion = { questionText: qMatch[1].trim(), followups: [] };
    } else if (fMatch && currentQuestion) {
      currentQuestion.followups.push(fMatch[1].trim());
    } else if (!hasQPrefix) {
      if (currentQuestion) questions.push(currentQuestion);
      currentQuestion = { questionText: trimmed, followups: [] };
    }
  }

  if (currentQuestion) questions.push(currentQuestion);
  return questions;
};

/**
 * Parse an Excel/CSV file buffer into question objects.
 * Detects media columns and extracts {{file_key|type|name}} metadata for re-import.
 */
export const parseExcelImport = (buffer) => {
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

  if (!rows || rows.length === 0) return [];

  // Detect header row
  const firstRow = rows[0];
  let startRow = 0;
  if (typeof firstRow[0] === 'string' &&
    (firstRow[0].toLowerCase() === '#' || firstRow[0].toLowerCase() === 'number' ||
     firstRow[0].toLowerCase() === 'question' || firstRow[1]?.toString().toLowerCase() === 'question')) {
    startRow = 1;
  }

  // Detect if first column is numeric
  const hasNumberColumn = startRow < rows.length && typeof rows[startRow][0] === 'number';
  const questionColIdx = hasNumberColumn ? 1 : 0;

  // Build column map — pair text columns with their adjacent media columns
  let qMediaColIdx = null;
  const followupPairs = []; // { textIdx, mediaIdx }

  if (startRow === 1) {
    for (let c = questionColIdx + 1; c < firstRow.length; c++) {
      const header = String(firstRow[c] || '').toLowerCase().trim();
      if (header.includes('media')) {
        // Q Media column (first media column right after question)
        if (header.startsWith('q') && qMediaColIdx === null) {
          qMediaColIdx = c;
        }
        // Other media columns are already paired via the text column loop below
      } else {
        // Followup text column — check if next column is its media pair
        const nextC = c + 1;
        const nextHeader = nextC < firstRow.length ? String(firstRow[nextC] || '').toLowerCase().trim() : '';
        followupPairs.push({
          textIdx: c,
          mediaIdx: nextHeader.includes('media') ? nextC : null,
        });
      }
    }
  } else {
    // No header row — all columns after question are text followups
    if (rows.length > 0) {
      for (let c = questionColIdx + 1; c < rows[0].length; c++) {
        followupPairs.push({ textIdx: c, mediaIdx: null });
      }
    }
  }

  const questions = [];
  for (let i = startRow; i < rows.length; i++) {
    const row = rows[i];
    const questionText = String(row[questionColIdx] || '').trim();
    if (!questionText) continue;

    const question = { questionText, followups: [] };

    // Q media
    if (qMediaColIdx !== null) {
      const qMedia = parseMediaMeta(String(row[qMediaColIdx] || '').trim());
      if (qMedia) question.media = qMedia;
    }

    // Followups with optional media
    for (const pair of followupPairs) {
      const cellValue = String(row[pair.textIdx] || '').trim();
      if (!cellValue) continue;
      if (pair.mediaIdx !== null) {
        const fMedia = parseMediaMeta(String(row[pair.mediaIdx] || '').trim());
        if (fMedia) {
          question.followups.push({ text: cellValue, media: fMedia });
          continue;
        }
      }
      question.followups.push(cellValue);
    }

    questions.push(question);
  }
  return questions;
};

/**
 * Read and parse an uploaded file, returning parsed questions.
 */
export const parseImportFile = (file) => {
  return new Promise((resolve) => {
    if (!file) return resolve({ questions: [], error: 'No file selected.' });
    if (file.size > 5 * 1024 * 1024) return resolve({ questions: [], error: 'File is too large. Max 5MB.' });

    const name = file.name.toLowerCase();
    const isText = name.endsWith('.txt');
    const isExcel = name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.csv');

    if (!isText && !isExcel) {
      return resolve({ questions: [], error: 'Unsupported file type. Use .txt, .csv, .xls, or .xlsx.' });
    }

    const reader = new FileReader();
    reader.onerror = () => resolve({ questions: [], error: 'Failed to read the file.' });

    if (isText) {
      reader.onload = (e) => {
        try {
          const questions = parseTextImport(e.target.result);
          resolve(questions.length ? { questions, error: null } : { questions: [], error: 'No valid questions found.' });
        } catch { resolve({ questions: [], error: 'Failed to parse the text file.' }); }
      };
      reader.readAsText(file);
    } else {
      reader.onload = (e) => {
        try {
          const questions = parseExcelImport(new Uint8Array(e.target.result));
          resolve(questions.length ? { questions, error: null } : { questions: [], error: 'No valid questions found.' });
        } catch { resolve({ questions: [], error: 'Failed to parse the file.' }); }
      };
      reader.readAsArrayBuffer(file);
    }
  });
};

// Helpers
const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const escapeHtml = (str) => {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
};

const sanitizeFilename = (name) => (name || 'questions').replace(/[^a-zA-Z0-9_\- ]/g, '').replace(/\s+/g, '_');

/** Build media cell/line value: URL + {{file_key|media_type|filename}} metadata tag */
const buildMediaCellValue = (url, media) => {
  let val = url || '';
  if (media?.file_key) {
    val += `${val ? ' ' : ''}{{${media.file_key}|${media.media_type}|${media.filename}}}`;
  }
  return val;
};

/** Extract media metadata from a cell/line value containing {{file_key|media_type|filename}} */
const parseMediaMeta = (value) => {
  if (!value) return null;
  const match = value.match(/\{\{(.+?)\}\}/);
  if (!match) return null;
  const parts = match[1].split('|');
  if (parts.length < 2) return null;
  return { file_key: parts[0], media_type: parts[1], filename: parts[2] || '' };
};
